import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { APIResponse } from './types/api-response.type';

export enum FeatureFlagsEnum {
  HEALTH_RADAR = 'health_radar',
}

export type FeatureFlags = Record<string, boolean>;

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private featureFlags: FeatureFlags = {};

  private flagsLoaded = false;

  private flagsLoading$: Observable<FeatureFlags> | null = null;

  constructor(private httpClient: HttpClient) {}

  /**
   * Loads feature flags from the API
   * This method is called during app initialization
   */
  loadFeatureFlags(): Observable<FeatureFlags> {
    // If flags are already loaded, return them immediately
    if (this.flagsLoaded) {
      return of(this.featureFlags);
    }

    // If a request is already in progress, return that observable
    if (this.flagsLoading$) {
      return this.flagsLoading$;
    }

    // Make the API call
    const url = `${environment.ROLEAPIENDPOINT}get/feature-flags`;
    this.flagsLoading$ = this.httpClient.get<APIResponse<{ flags: FeatureFlags }>>(url).pipe(
      map((response) => {
        if (response.data) {
          this.featureFlags = response.data.flags;
          this.flagsLoaded = true;
          return this.featureFlags;
        }
        // If no data in response, return empty object
        this.featureFlags = {};
        this.flagsLoaded = true;
        return this.featureFlags;
      }),
      catchError((error) => {
        console.error('Error loading feature flags:', error);
        // On error, return empty object so app can continue
        this.featureFlags = {};
        this.flagsLoaded = true;
        return of(this.featureFlags);
      }),
      shareReplay(1), // Cache the result
    );

    return this.flagsLoading$;
  }

  /**
   * Checks if a feature flag is active (default to true if not set)
   * @param featureName - The name of the feature flag to check
   * @returns true if the feature is active, false otherwise
   */
  isFeatureActive(featureName: FeatureFlagsEnum): boolean {
    return this.featureFlags[featureName] === true;
  }

  /**
   * Gets all feature flags
   * @returns A copy of the feature flags dictionary
   */
  getAllFlags(): FeatureFlags {
    return { ...this.featureFlags };
  }

  /**
   * Checks if flags have been loaded
   * @returns true if flags have been loaded
   */
  areFlagsLoaded(): boolean {
    return this.flagsLoaded;
  }
}
